import { Component, inject, signal } from '@angular/core';
import { CacTableComponent, CacTableColumnSelectorComponent, table } from '@controladad/ng-base';
import { OrderApiService, BrandApiService, CategoryApiService, KwdCurrencyPipe } from '@core';
import { OrderItemReportResponseDTO } from '@models';

@Component({
  selector: 'lib-order-items-report',
  imports: [CacTableComponent, CacTableColumnSelectorComponent, KwdCurrencyPipe],
  templateUrl: './order-items-report.component.html',
  styleUrl: './order-items-report.component.scss',
})
export class OrderItemsReportComponent {
  private readonly orderApi = inject(OrderApiService);
  private readonly brandApi = inject(BrandApiService);
  private readonly categoryApi = inject(CategoryApiService);

  totalQuantity = signal(0);
  totalPrice = signal(0);

  table = table({
    itemsFn: this.orderApi.getOrderItemReportAdapter({
      mapFn: (response: { data: OrderItemReportResponseDTO[]; pagination: any }) => {
        // The API returns data wrapped in an array: {data: [{items: [], totalQuantity: ..., totalPrice: ...}]}
        const reportData = response?.data?.[0];
        this.totalQuantity.set(reportData?.totalQuantity || 0);
        this.totalPrice.set(reportData?.totalPrice || 0);
        
        return {
          data: reportData?.items || [],
          pagination: {
            page: response.pagination?.page || 1,
            size: response.pagination?.amountOfListPerPage || 10,
            total: response.pagination?.total || 0,
            totalPages: response.pagination?.pageCount || 1,
          },
        };
      }
    }),
    view: { itemName: 'Order Item' },
    columns: {
      orderId: {
        label: 'Order ID',
        sortable: 'OrderId',
        filterable: [
          {
            type: 'contains',
            key: 'OrderIds',
          },
        ],
      },
      orderTime: {
        label: 'Order Time',
        type: 'datetime',
        sortable: 'OrderTime',
        filterable: [
          { type: 'greater', label: 'From', controlType: 'datetime', key: 'OrderTimeFrom' },
          { type: 'lower', label: 'To', controlType: 'datetime', key: 'OrderTimeTo' },
        ],
      },
      'image.url': {
        label: 'Thumbnail',
        type: 'image',
      },
      englishProductName: {
        label: 'Product Name (EN)',
        sortable: 'EnglishProductName',
        filterable: [
          {
            type: 'contains',
            key: 'EnglishProductName',
          },
        ],
      },
      arabicProductName: {
        label: 'Product Name (AR)',
        sortable: 'ArabicProductName',
        filterable: [
          {
            type: 'contains',
            key: 'ArabicProductName',
          },
        ],
      },
      sku: {
        label: 'SKU',
        sortable: 'SKU',
        filterable: [
          {
            type: 'contains',
            key: 'SKU',
          },
        ],
      },
      brand: {
        label: 'Brand',
        sortable: 'Brand',
        filterable: [
          {
            type: 'equal',
            controlType: 'select',
            multiple: true,
            key: 'Brands',
            items: this.brandApi.getBrandItems(),
          },
        ],
      },
      primaryCategory: {
        label: 'Category',
        sortable: 'PrimaryCategory',
        filterable: [
          {
            type: 'equal',
            controlType: 'select',
            multiple: true,
            key: 'PrimaryCategories',
            items: this.categoryApi.getFlatCategories(),
          },
        ],
      },
      vendor: {
        label: 'Vendor',
        sortable: 'Vendor',
        filterable: [
          {
            type: 'equal',
            controlType: 'select',
            multiple: true,
            key: 'Vendors',
            items: this.orderApi.getAllVendors(),
          },
        ],
      },
      soldPrice: {
        label: 'Price',
        type: 'text',
        sortable: 'SoldPrice',
        transform: (value) => value != null ? `${value.toFixed(3)} KWD` : '-',
        filterable: [
          { type: 'greater', label: 'Min Price', controlType: 'input', inputType: 'number', key: 'SoldPriceFrom' },
          { type: 'lower', label: 'Max Price', controlType: 'input', inputType: 'number', key: 'SoldPriceTo' },
        ],
      },
      quantity: {
        label: 'Quantity',
        sortable: 'Quantity',
        filterable: [
          { type: 'greater', label: 'Min Quantity', controlType: 'input', inputType: 'number', key: 'QuantityFrom' },
          { type: 'lower', label: 'Max Quantity', controlType: 'input', inputType: 'number', key: 'QuantityTo' },
        ],
      },
      itemCode: {
        label: 'Item Code',
        sortable: 'ItemCode',
        filterable: [
          {
            type: 'contains',
            key: 'ItemCode',
          },
        ],
      },
      article: {
        label: 'Article',
        sortable: 'Article',
        filterable: [
          {
            type: 'contains',
            key: 'Article',
          },
        ],
      },
    },
    export: (filters) => this.orderApi.exportOrderItemReport(filters),
  });
}
